import { useState } from 'react';
import cns from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Table, TableColumn } from '@zep/ui';
import Empty from '../../components/service/common/Empty';
import { useDonations } from '../../services';
import { Donation } from '../../types/DonationTypes';
import { getSentHistoryDetail, getSentHistoryTypeTranslationKey } from './donationHistoryPresentation';
import S from './Donations.module.scss';
enum ViewType {
  RECEIVED = 'received',
  SENT = 'sent',
}
function isViewType(value: unknown): value is ViewType {
  return typeof value === 'string' && Object.values(ViewType).includes(value as ViewType);
}
const DONATION_TYPE_ZEM = '0';
export const DonationsPageView = () => {
  const {
    query
  } = useRouter();
  const {
    t
  } = useTranslation(['manage', 'wearables']);
  const [viewType, setViewType] = useState(isViewType(query.viewType) ? query.viewType : ViewType.RECEIVED);
  const [page, setPage] = useState(1);
  const {
    data: donationData
  } = useDonations(viewType, DONATION_TYPE_ZEM, page, 10);
  const isReceived = viewType === ViewType.RECEIVED;
  const columns: TableColumn<Donation>[] = isReceived ? [{
    title: t('manage:donate_table_userid'),
    dataIndex: 'playerName',
    width: '25%'
  }, {
    title: t('manage:donate_table_amount'),
    dataIndex: 'amount',
    width: '20%',
    render: ({
      amount
    }) => `${amount.numberFormat()} ZEM`
  }, {
    title: t('manage:donate_table_spacename'),
    dataIndex: 'spaceName',
    width: '30%'
  }, {
    title: t('manage:donate_table_time'),
    dataIndex: 'createdAt',
    width: '25%',
    render: item => {
      const createdAt = new Date(item?.createdAt);
      createdAt.setMinutes(createdAt.getMinutes() - createdAt.getTimezoneOffset());
      return format(createdAt, 'yyyy-MM-dd aa hh:mm:ss');
    }
  }] : [{
    title: t('manage:zem_history_table_type' as never),
    dataIndex: 'historyType',
    width: '18%',
    render: item => t(getSentHistoryTypeTranslationKey(item) as never)
  }, {
    title: t('manage:zem_history_table_detail' as never),
    dataIndex: 'targetPlayerName',
    width: '27%',
    render: item => {
      const detail = getSentHistoryDetail(item);
      return detail.translationKey ? t(detail.translationKey as never, {
        defaultValue: detail.defaultValue
      }) : detail.text;
    }
  }, {
    title: t('manage:donate_table_amount'),
    dataIndex: 'amount',
    width: '18%',
    render: ({
      amount
    }) => `${amount.numberFormat()} ZEM`
  }, {
    title: t('manage:donate_table_spacename'),
    dataIndex: 'spaceName',
    width: '20%',
    render: item => item.spaceName || '-'
  }, {
    title: t('manage:donate_table_time'),
    dataIndex: 'createdAt',
    width: '17%',
    render: item => {
      const createdAt = new Date(item?.createdAt);
      createdAt.setMinutes(createdAt.getMinutes() - createdAt.getTimezoneOffset());
      return format(createdAt, 'yyyy-MM-dd aa hh:mm:ss');
    }
  }];
  return <section className="contents" data-sentry-component="DonationsPageView" data-sentry-source-file="donations.tsx">
      <div className="container">
        <h3 className={S.title}>{t('manage:zem_history_title' as never)}</h3>
        <div className={S.header}>
          <ul>
            {Object.values(ViewType).map(type => <li key={type} className={cns({
            [S.active_item]: type === viewType
          })} onClick={() => {
            setViewType(type);
            setPage(1);
          }}>
                {type === ViewType.RECEIVED ? t('manage:zem_history_received' as never) : t('manage:zem_history_used' as never)}
              </li>)}
          </ul>
        </div>
        <div className={S.table_container}>
          {donationData?.donations.length ? <Table columns={columns} data={donationData.donations} pagination={{
          nowPageNo: page,
          lastPageNo: donationData?.pager.totalPages || 0,
          onPageChange: setPage
        }} /> : <div className={S.empty_wrapper}>
              <Empty emptyItem={isReceived ? t('manage:gift_history_received_empty') : t('manage:zem_history_used_empty' as never)} useCustomMessage image={{
            url: '/images/light/donations/empty_zem-gray400.svg',
            width: 125,
            height: 125
          }} />
            </div>}
        </div>
      </div>
    </section>;
};